import { Alert, Button, Card, Form, Input, Select, Space, message } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from './components/EducationPageChrome';
import { EducationGradeSelect, EducationSubjectSelect } from './components/EducationLookupSelects';

type Item = Record<string, unknown> & { id: string };

export default function QuestionItemFormPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const item = useQuery({
    queryKey: ['education-question-item', id],
    enabled: Boolean(id),
    queryFn: async () => (await api.get<Item>(`/education/questions/${id}`)).data,
  });
  if (item.data && !form.isFieldsTouched()) form.setFieldsValue(item.data);
  const save = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      id ? api.put(`/education/questions/${id}`, values) : api.post('/education/questions', values),
    onSuccess: () => {
      message.success(t('saved'));
      navigate('/education/questions/items');
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });
  return (
    <EducationPageChrome title={t(id ? 'editQuestionItem' : 'newQuestionItem')}>
      <Alert type="info" showIcon message={t('questionBankGuardrail')} style={{ marginBottom: 16 }} />
      <Card size="small" loading={item.isLoading} style={{ maxWidth: 860 }}>
        <Form form={form} layout="vertical" initialValues={{ item_type: 'short', status: 'draft' }} onFinish={(values) => save.mutate(values)}>
          <Form.Item name="code" label={t('code')} rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="subject_id" label={t('subject')} rules={[{ required: true }]}><EducationSubjectSelect /></Form.Item>
          <Form.Item name="grade_level_id" label={t('grade')}><EducationGradeSelect allowClear /></Form.Item>
          <Form.Item name="stem_text" label={t('questionStem')} rules={[{ required: true }]}><Input.TextArea rows={6} /></Form.Item>
          <Form.Item name="item_type" label={t('itemType')} rules={[{ required: true }]}>
            <Select options={['mcq', 'short', 'long', 'practical'].map((value) => ({ value, label: t(`questionType_${value}`) }))} />
          </Form.Item>
          <Form.Item name="difficulty" label={t('difficulty')}><Input /></Form.Item>
          <Form.Item name="cognitive_level" label={t('cognitiveLevel')}><Input /></Form.Item>
          <Form.Item name="status" label={t('status')} rules={[{ required: true }]}>
            <Select options={['draft', 'active', 'retired'].map((value) => ({ value, label: t(value) }))} />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={save.isPending}>{t('save')}</Button>
            <Button onClick={() => navigate('/education/questions/items')}>{t('cancel')}</Button>
          </Space>
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
